import React, { useState, useEffect, useMemo } from "react";
import { connect, useSelector } from "react-redux";
import { createSelector } from "reselect";
import Config from "../../../../config/main";
import Helpers from "../../../../helpers/helperFunctions";
import { GetCurrencyConfig, GetOddsFormat, GetTerminalBalance } from "../../../../helpers/selectors";
import OddConverter from "@common/helpers/oddConverter";
import { t } from "../../../../helpers/translator";
import Zergling from "../../../../helpers/zergling";
import Keyboard from "../../../components/virtualKeyboard";
import MoneyAmount from "../../../components/moneyAmount";
import { createPredefinedBet } from "../../../../helpers/terminalRequests/predefinedMultiples";
import { PrintTicket } from "../../../../actions/terminal";
import { getPredefinedMultipleExpressBonus, changeTicketResultModel, changeTicketResultModelNested } from "../service";
import Loader from "../../loader";
import { roundStake } from "../../../../helpers/sport/betslip";

const MultiplesItemPlaceBet = ({
	token,
	totalOdd,
	events,
	multiple,
	dispatch,
	currencyConfig,
	oddsFormat,
	partner,
	bonusPercent,
}) => {
	const [stake, setStake] = useState();
	const [loading, setLoading] = useState();
	const [errorMsg, setErrorMsg] = useState();
	const balance = useSelector(GetTerminalBalance);
	const finalBalance = balance || JSON.parse(sessionStorage.getItem("balance"))?.session_balance;
	const [keyboardSettings, setKeyboardSettings] = useState();
	const currency = useMemo(() => JSON.parse(sessionStorage.getItem("authData")).currency, []);

	const getSelections = () =>
		multiple.Selections.map(item => ({
			SelectionId: item.SelectionId,
			Price: events?.find(event => event.id === item.SelectionId)?.price,
		}));

	const possWin = useMemo(() => {
		if (!stake) return null;
		const rounding = Math.abs(currencyConfig.rounding);
		const amount = stake * totalOdd;
		return parseFloat(amount.toFixed(rounding));
	}, [stake, totalOdd]);

	const taxAmount = useMemo(
		() =>
			stake &&
			Helpers.calculateTaxForReturnedAndProfit(
				partner?.taxPercent,
				partner?.tax_type === 2 ? possWin : possWin - stake,
				partner?.tax_amount_ranges
			),
		[stake]
	);

	const estimatedReturn = useMemo(() => {
		if (!possWin) return null;
		const bonus = getPredefinedMultipleExpressBonus(multiple, getSelections(), stake, currencyConfig);
		return possWin + bonus;
	}, [possWin, currencyConfig]);

	const getMaxStake = async () => {
		const params = {
			selection_list: multiple.Selections.map(item => ({ SelectionId: item.SelectionId })),
			type_id: multiple.BetType,
			token,
		};

		const { data: { MaxStake: maxStake } = {} } = await Zergling.get(params, "terminal_get_bet_max_stake");
		maxStake && setStake(maxStake);
	};
	const bonus = getPredefinedMultipleExpressBonus(multiple, getSelections(), stake, currencyConfig);

	useEffect(() => {
		keyboardSettings && document.addEventListener("click", toggleKeyboard);
		return () => document.removeEventListener("click", toggleKeyboard);
	}, [keyboardSettings]);

	const toggleKeyboard = e => {
		if (keyboardSettings) !e.defaultPrevented && setKeyboardSettings(null);
		else {
			const keyboardSettings = {
				currentValue: stake,
				changeHandler: setStake,
				processHandler: stake =>
					parseFloat(stake).toString() === stake
						? roundStake(stake, currencyConfig.rounding)
						: stake,
				offset: e.target.offsetTop,
			};

			setKeyboardSettings(keyboardSettings);
		}
	};

	const placeBet = async () => {
		setLoading(true);
		setErrorMsg("");

		const data = await Zergling.get(
			createPredefinedBet(stake, multiple.Id, getSelections()),
			"betshop_command"
		);

		if (data.data?.Message) setErrorMsg(data.data?.Message);
		else {
			const ticketResult = changeTicketResultModel(data.data[0]);

			dispatch(
				PrintTicket(
					JSON.stringify({
						...ticketResult,
						price: OddConverter(ticketResult.price, oddsFormat),
						selection_list: ticketResult.selection_list.map(item => ({
							...item,
							price: OddConverter(item.price, oddsFormat),
						})),
					})
				)
			);
		}

		setLoading(false);
		setStake();
	};

	const isPlaceBetDisabled = !stake || stake > finalBalance;

	return (
		<div className="place-bet-container">
			{keyboardSettings && <Keyboard settings={keyboardSettings} bindToElement={false} />}

			<div className="bottom-block">
				<div>
					{t("Multiple:")} <span>{events?.length}</span>
				</div>
				<div>
					{t("Odds")}
					<span> {OddConverter(totalOdd, oddsFormat)}</span>
				</div>
			</div>

			{errorMsg && <p className="error-msg">{errorMsg}</p>}

			<div onClick={toggleKeyboard} className="single-form-item">
				<span>{stake || t("Stake...")}</span>
			</div>
			{!!possWin && (
				<div>
					<div className="possible-win">
						{t("Possible win:")}&nbsp;
						<span>
							<MoneyAmount amount={possWin} />
						</span>
					</div>
					<div className="bonus">
						{t("Accumulator Bonus")}&nbsp;
						<span>
							{Config.enableCurrencyView && (
								<span>{Config.main.currency[currency]}</span>
							)}
							<i>{bonus}</i>
						</span>
					</div>

					<div className="estimated-return">
						{t("Estimated return:")}&nbsp;
						<span>
							<MoneyAmount amount={estimatedReturn} />
						</span>
					</div>
					{!!taxAmount && (
						<div className="possible-win">
							{t("Tax on Return:")}&nbsp;
							<span>
								<MoneyAmount amount={taxAmount} />
							</span>
						</div>
					)}

					{taxAmount && (bonus || Config.betting.showTotalWinWithoutBonus) ? (
						<div className="possible-win">
							{!Config.betslipTextsCircus ? t("Total win:") : t("Gain max")}
							&nbsp;
							<span>
								<MoneyAmount
									amount={
										Config.estimatedReturnAndTotalWinCalculationChange
											? possWin +
											  (bonus || 0) -
											  taxAmount
											: possWin + (bonus || 0)
									}
								/>
							</span>
						</div>
					) : null}
				</div>
			)}
			<div className="mini-table-b-m">
				<ul>
					<li className={`staticStakeMax`} onClick={getMaxStake}>
						<div className="max-bet-b-m multipleMaxBet">
							<span>{t("Max bet")}</span>
						</div>
					</li>
				</ul>
			</div>

			<div className="separator-box-buttons-m multiplePlaceBet">
				<button
					className="button-view-normal-m"
					disabled={isPlaceBetDisabled}
					onClick={placeBet}
				>
					{t("Place bet")}
				</button>
			</div>

			{loading && <Loader component="predefinedMultiples-multiplesItemPlaceBet" />}
		</div>
	);
};

const mapStateToProps = _ =>
	createSelector(
		[
			GetOddsFormat,
			state => state.terminal.authData.token,
			state => state.swarmConfigData.partner,
			GetCurrencyConfig,
		],
		(oddsFormat, token, partner, currencyConfig) => ({ oddsFormat, token, partner, currencyConfig })
	);

export default connect(mapStateToProps)(MultiplesItemPlaceBet);
